import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

interface SavedPreset<T> {
	name: string;
	value: T;
}

interface Props<T> {
	storageNamespace: string;
	noun: string;
	currentValue: T;
	canSave?: boolean;
	parseValue: (value: unknown) => T | null;
	onLoad: (value: T) => void;
	formatOptionLabel?: (name: string, value: T) => React.ReactNode;
}

function readPresets<T>(
	storageKey: string,
	parseValue: (value: unknown) => T | null,
): SavedPreset<T>[] {
	try {
		const storedValue = window.localStorage.getItem(storageKey);
		if (!storedValue) return [];

		const parsed: unknown = JSON.parse(storedValue);
		if (!Array.isArray(parsed)) return [];

		return parsed.flatMap((candidate) => {
			if (!candidate || typeof candidate !== 'object') return [];
			const storedPreset = candidate as { name?: unknown; value?: unknown };
			const name = typeof storedPreset.name === 'string' ? storedPreset.name.trim() : '';
			const value = parseValue(storedPreset.value);
			return name && value !== null ? [{ name, value }] : [];
		});
	} catch {
		return [];
	}
}

export default function SavedPresetControls<T>({
	storageNamespace,
	noun,
	currentValue,
	canSave = true,
	parseValue,
	onLoad,
	formatOptionLabel = (name) => name,
}: Props<T>) {
	const { user } = useAuth0();
	const storageOwner = user?.sub ?? user?.email;
	const storageKey = storageOwner ? `${storageNamespace}:${storageOwner}` : null;
	const titleNoun = `${noun.charAt(0).toUpperCase()}${noun.slice(1)}`;
	const [presets, setPresets] = React.useState<SavedPreset<T>[]>([]);
	const [presetName, setPresetName] = React.useState('');
	const [selectedName, setSelectedName] = React.useState('');
	const [deleteConfirmationOpen, setDeleteConfirmationOpen] = React.useState(false);
	const [storageError, setStorageError] = React.useState<string | null>(null);

	React.useEffect(() => {
		setPresets(storageKey ? readPresets(storageKey, parseValue) : []);
		setPresetName('');
		setSelectedName('');
		setDeleteConfirmationOpen(false);
		setStorageError(null);
	}, [parseValue, storageKey]);

	const selectedPreset = presets.find((preset) => preset.name === selectedName);
	const saveDisabled = !storageKey || !canSave || !presetName.trim();

	const handleSave = () => {
		if (!storageKey || saveDisabled) return;
		const name = presetName.trim();
		const existingIndex = presets.findIndex(
			(preset) => preset.name.toLowerCase() === name.toLowerCase(),
		);
		const nextPreset = { name, value: currentValue };
		const nextPresets = existingIndex === -1
			? [...presets, nextPreset]
			: presets.map((preset, index) => (index === existingIndex ? nextPreset : preset));

		try {
			window.localStorage.setItem(storageKey, JSON.stringify(nextPresets));
			setPresets(nextPresets);
			setSelectedName(name);
			setDeleteConfirmationOpen(false);
			setStorageError(null);
		} catch {
			setStorageError(`Saved ${noun}s could not be updated in this browser.`);
		}
	};

	const handleSelect = (name: string) => {
		const preset = presets.find((candidate) => candidate.name === name);
		setSelectedName(name);
		setDeleteConfirmationOpen(false);
		if (!preset) return;
		setPresetName(preset.name);
		onLoad(preset.value);
	};

	const handleDelete = () => {
		if (!storageKey || !selectedPreset) return;
		const nextPresets = presets.filter((preset) => preset.name !== selectedPreset.name);
		try {
			window.localStorage.setItem(storageKey, JSON.stringify(nextPresets));
			setPresets(nextPresets);
			setPresetName('');
			setSelectedName('');
			setStorageError(null);
		} catch {
			setStorageError(`Saved ${noun}s could not be updated in this browser.`);
		}
		setDeleteConfirmationOpen(false);
	};

	return (
		<>
			<Stack spacing={0.75} sx={{ minWidth: 0 }}>
				<Stack
					direction="row"
					spacing={0.75}
					useFlexGap
					sx={{ alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}
				>
					<TextField
						label={`${titleNoun} name`}
						value={presetName}
						onChange={(event) => setPresetName(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === 'Enter' && !saveDisabled) handleSave();
						}}
						size="small"
						inputProps={{ maxLength: 80 }}
						sx={{ flex: 1, minWidth: 180 }}
					/>
					<TextField
						select
						label={`Saved ${noun}s`}
						value={selectedName}
						onChange={(event) => handleSelect(event.target.value)}
						size="small"
						sx={{ flex: 1, minWidth: 180 }}
					>
						<MenuItem value="" disabled>No saved {noun}s</MenuItem>
						{presets.map((preset) => (
							<MenuItem key={preset.name} value={preset.name}>
								{formatOptionLabel(preset.name, preset.value)}
							</MenuItem>
						))}
					</TextField>
					<Button
						variant="contained"
						size="small"
						disabled={saveDisabled}
						onClick={handleSave}
						startIcon={<SaveIcon />}
						sx={{ whiteSpace: 'nowrap' }}
					>
						Save
					</Button>
					<Tooltip title={`Delete saved ${noun}`}>
						<span>
							<IconButton
								color="error"
								size="small"
								disabled={!selectedPreset}
								onClick={() => setDeleteConfirmationOpen(true)}
								aria-label={`Delete saved ${noun}`}
							>
								<DeleteOutlineIcon />
							</IconButton>
						</span>
					</Tooltip>
				</Stack>
				{storageError && (
					<Typography variant="caption" color="error.main">{storageError}</Typography>
				)}
			</Stack>
			<Dialog
				open={deleteConfirmationOpen && selectedPreset !== undefined}
				onClose={() => setDeleteConfirmationOpen(false)}
				maxWidth="xs"
				fullWidth
				PaperProps={{ sx: { backgroundImage: 'none' } }}
			>
				<DialogTitle>Delete Saved {titleNoun}</DialogTitle>
				<DialogContent>
					<Typography>
						Delete <strong>{selectedPreset?.name}</strong>?
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteConfirmationOpen(false)}>Cancel</Button>
					<Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
